import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, DownloadIcon, UserPlusIcon, MapPinIcon, MailIcon, PhoneIcon, StarIcon, CheckCircle2Icon, CheckIcon, XIcon } from 'lucide-react';
type Step = 'search' | 'enrich' | 'export';
const searchResults = [{
  name: 'Brooklyn Roasting Company',
  category: 'Coffee shop',
  location: 'Brooklyn, NY',
  email: 'info@brooklynroasting.com',
  phone: '(718) 555-0192',
  rating: 4.6
}, {
  name: 'Devocion',
  category: 'Coffee shop',
  location: 'Williamsburg, NY',
  email: 'hello@devocion.com',
  phone: '(718) 555-0881',
  rating: 4.8
}, {
  name: 'Sey Coffee',
  category: 'Coffee shop',
  location: 'Bushwick, NY',
  email: 'contact@seycoffee.com',
  phone: '(347) 555-0442',
  rating: 4.7
}];
export function ScraperDemo() {
  const [activeStep, setActiveStep] = useState<Step>('search');
  const [searchPhase, setSearchPhase] = useState<'loading' | 'done'>('loading');
  const [enrichPhase, setEnrichPhase] = useState<'loading' | 'done'>('loading');
  const [exportPhase, setExportPhase] = useState<'loading' | 'done'>('loading');
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const enrichTimerRef = useRef<NodeJS.Timeout | null>(null);
  const exportTimerRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    startSearch();
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (enrichTimerRef.current) clearTimeout(enrichTimerRef.current);
      if (exportTimerRef.current) clearTimeout(exportTimerRef.current);
    };
  }, []);
  const startSearch = () => {
    setSearchPhase('loading');
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearchPhase('done');
    }, 1200);
  };
  const startEnrich = () => {
    setEnrichPhase('loading');
    if (enrichTimerRef.current) clearTimeout(enrichTimerRef.current);
    enrichTimerRef.current = setTimeout(() => {
      setEnrichPhase('done');
    }, 1400);
  };
  const startExport = () => {
    setExportPhase('loading');
    if (exportTimerRef.current) clearTimeout(exportTimerRef.current);
    exportTimerRef.current = setTimeout(() => {
      setExportPhase('done');
    }, 1500);
  };
  const handleTabSwitch = (step: Step) => {
    setActiveStep(step);
    if (step === 'search') startSearch();
    if (step === 'enrich') startEnrich();
    if (step === 'export') startExport();
  };
  return <section className="py-24 bg-white" data-id="element-1993">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1994">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1995">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-1996">
            See how Openmart powers{' '}
            <span className="text-blue-600" data-id="element-1997">Google Maps scraping</span>
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-1998">
            Search local businesses by keyword and location, enrich with
            decision-maker contacts, then export data ready for outreach.
          </p>
        </div>

        {/* Step tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-14" data-id="element-1999">
          <button onClick={() => handleTabSwitch('search')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'search' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-2000">
            <SearchIcon className="w-4 h-4" data-id="element-2001" />
            Search Google Maps
          </button>
          <button onClick={() => handleTabSwitch('enrich')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'enrich' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-2002">
            <UserPlusIcon className="w-4 h-4" data-id="element-2003" />
            Find decision makers
          </button>
          <button onClick={() => handleTabSwitch('export')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'export' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-2004">
            <DownloadIcon className="w-4 h-4" data-id="element-2005" />
            Export data
          </button>
        </div>

        <AnimatePresence mode="wait" data-id="element-2006">
          {activeStep === 'search' && <motion.div key="search" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-2007">
              {/* Left: Search form */}
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-2008">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-2009">
                  Search Google Maps
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-2010">
                  Define your target businesses by category and location.
                </p>

                <div className="space-y-5" data-id="element-2011">
                  <div data-id="element-2012">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2013">
                      Business type
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-2014">
                      Coffee shops
                    </div>
                  </div>
                  <div data-id="element-2015">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2016">
                      Location
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-2017">
                      Brooklyn, NY
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-2018">
                  {searchPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-2019">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-2020" />
                      Scraping Google Maps...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-2021">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-2022" />
                      342 businesses found
                    </span>}
                </div>
              </div>

              {/* Right: Results */}
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-2023">
                {searchPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-2024">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-2025" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-2026">
                      Scraping coffee shops in Brooklyn...
                    </p>
                    <p className="text-sm text-slate-400 mt-1" data-id="element-2027">
                      Extracting emails and phone numbers
                    </p>
                  </div>}

                {searchPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-2028">
                    <div className="flex items-center justify-between mb-6" data-id="element-2029">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-2030">
                        Results
                      </h3>
                      <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full" data-id="element-2031">
                        342 businesses found
                      </span>
                    </div>

                    <div className="space-y-3" data-id="element-2032">
                      {searchResults.map((result, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.15,
                  duration: 0.3
                }} className="bg-white rounded-2xl p-5 shadow-sm" data-id="element-2033">
                          <div className="flex items-start justify-between mb-3" data-id="element-2034">
                            <div data-id="element-2035">
                              <h4 className="text-base font-bold text-slate-900" data-id="element-2036">
                                {result.name}
                              </h4>
                              <div className="flex items-center gap-3 mt-1" data-id="element-2037">
                                <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-2038">
                                  <MapPinIcon className="w-3 h-3" data-id="element-2039" />
                                  {result.location}
                                </span>
                                <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-2040">
                                  <StarIcon className="w-3 h-3 fill-yellow-400 text-yellow-400" data-id="element-2041" />
                                  {result.rating}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-slate-600" data-id="element-2042">
                            <span className="flex items-center gap-1.5" data-id="element-2043">
                              <MailIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-2044" />
                              {result.email}
                            </span>
                            <span className="flex items-center gap-1.5" data-id="element-2045">
                              <PhoneIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-2046" />
                              {result.phone}
                            </span>
                          </div>
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'enrich' && <motion.div key="enrich" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-2047">
              {/* Left: Enrich config */}
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-2048">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-2049">
                  Find decision makers
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-2050">
                  Get owner contacts that Google Maps doesn't show — our unique
                  advantage.
                </p>

                <div className="space-y-5" data-id="element-2051">
                  <div data-id="element-2052">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2053">
                      Enrich fields
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-2054">
                      <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg border border-blue-100" data-id="element-2055">
                        Owner name
                      </span>
                      <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg border border-blue-100" data-id="element-2056">
                        Owner email
                      </span>
                      <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg border border-blue-100" data-id="element-2057">
                        Direct phone
                      </span>
                      <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg border border-blue-100" data-id="element-2058">
                        Social profiles
                      </span>
                    </div>
                  </div>
                  <div data-id="element-2059">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2060">
                      Source
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-2061">
                      342 businesses from search
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-2062">
                  {enrichPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-2063">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-2064" />
                      Finding owners...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-2065">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-2066" />
                      289 owners found
                    </span>}
                </div>
              </div>

              {/* Right: Before/After enrichment */}
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-2067">
                {enrichPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-2068">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-2069" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-2070">
                      Finding business owners...
                    </p>
                    <p className="text-sm text-slate-400 mt-1" data-id="element-2071">
                      Matching decision-maker contacts
                    </p>
                  </div>}

                {enrichPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-2072">
                    <div className="flex items-center justify-between mb-6" data-id="element-2073">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-2074">
                        Enriched contacts
                      </h3>
                      <span className="text-sm font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full" data-id="element-2075">
                        289 of 342 owners found
                      </span>
                    </div>

                    <div className="space-y-3" data-id="element-2076">
                      {[{
                  name: 'Brooklyn Roasting Company',
                  location: 'Brooklyn, NY',
                  rating: 4.6,
                  owner: 'Jim Munson, Founder',
                  email: 'jim@brooklynroasting.com',
                  phone: '(718) 855-1000',
                  social: '@brooklynroasting'
                }, {
                  name: 'Devocion',
                  location: 'Williamsburg, NY',
                  rating: 4.8,
                  owner: 'Steven Sutton, CEO',
                  email: 'steven@devocion.com',
                  phone: '(718) 285-6180',
                  social: '@daboradevocion'
                }, {
                  name: 'Sey Coffee',
                  location: 'Bushwick, NY',
                  rating: 4.7,
                  owner: 'Tobin Lent, Co-founder',
                  email: 'tobin@seycoffee.com',
                  phone: '(347) 227-2553',
                  social: '@seycoffee'
                }].map((biz, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.15,
                  duration: 0.3
                }} className="bg-white rounded-2xl p-5 shadow-sm border border-blue-100" data-id="element-2077">
                          <div className="flex items-start justify-between mb-3" data-id="element-2078">
                            <div data-id="element-2079">
                              <h4 className="text-base font-bold text-slate-900" data-id="element-2080">
                                {biz.name}
                              </h4>
                              <div className="flex items-center gap-3 mt-1" data-id="element-2081">
                                <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-2082">
                                  <MapPinIcon className="w-3 h-3" data-id="element-2083" />
                                  {biz.location}
                                </span>
                                <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-2084">
                                  <StarIcon className="w-3 h-3 fill-yellow-400 text-yellow-400" data-id="element-2085" />
                                  {biz.rating}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="pt-3 mt-2 border-t border-slate-100" data-id="element-2086">
                            <div className="text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-2" data-id="element-2087">
                              Decision maker
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs" data-id="element-2088">
                              <span className="flex items-center gap-1.5" data-id="element-2089">
                                <CheckIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0" data-id="element-2090" />
                                <span className="text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded" data-id="element-2091">
                                  {biz.owner}
                                </span>
                              </span>
                              <span className="flex items-center gap-1.5" data-id="element-2092">
                                <CheckIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0" data-id="element-2093" />
                                <span className="text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded truncate" data-id="element-2094">
                                  {biz.email}
                                </span>
                              </span>
                              <span className="flex items-center gap-1.5" data-id="element-2095">
                                <CheckIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0" data-id="element-2096" />
                                <span className="text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded" data-id="element-2097">
                                  {biz.phone}
                                </span>
                              </span>
                              <span className="flex items-center gap-1.5" data-id="element-2098">
                                <CheckIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0" data-id="element-2099" />
                                <span className="text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded" data-id="element-2100">
                                  {biz.social}
                                </span>
                              </span>
                            </div>
                          </div>
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'export' && <motion.div key="export" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-2101">
              {/* Left: Export config */}
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-2102">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-2103">
                  Export data
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-2104">
                  Configure your Google Maps data export.
                </p>

                <div className="space-y-5" data-id="element-2105">
                  <div data-id="element-2106">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2107">
                      Export name
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-2108">
                      Brooklyn coffee shops Q1
                    </div>
                  </div>
                  <div data-id="element-2109">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2110">
                      Export format
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700 flex items-center justify-between" data-id="element-2111">
                      <span data-id="element-2112">CSV Download</span>
                      <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" data-id="element-2113">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" data-id="element-2114" />
                      </svg>
                    </div>
                    <div className="flex gap-2 mt-2" data-id="element-2115">
                      <span className="px-2.5 py-1 bg-slate-50 text-slate-500 text-xs font-medium rounded-lg border border-slate-200" data-id="element-2116">
                        Salesforce
                      </span>
                      <span className="px-2.5 py-1 bg-slate-50 text-slate-500 text-xs font-medium rounded-lg border border-slate-200" data-id="element-2117">
                        HubSpot
                      </span>
                      <span className="px-2.5 py-1 bg-slate-50 text-slate-500 text-xs font-medium rounded-lg border border-slate-200" data-id="element-2118">
                        Webhook
                      </span>
                    </div>
                  </div>
                  <div data-id="element-2119">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2120">
                      Include fields
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-2121">
                      <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg border border-blue-100" data-id="element-2122">
                        Email
                      </span>
                      <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg border border-blue-100" data-id="element-2123">
                        Phone
                      </span>
                      <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg border border-blue-100" data-id="element-2124">
                        Rating
                      </span>
                      <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg border border-blue-100" data-id="element-2125">
                        Reviews
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-2126">
                  {exportPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-2127">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-2128" />
                      Preparing export...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-2129">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-2130" />
                      342 records ready
                    </span>}
                </div>
              </div>

              {/* Right: Export result */}
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-2131">
                {exportPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-2132">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-2133" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-2134">
                      Preparing your export...
                    </p>
                    <p className="text-sm text-slate-400 mt-1" data-id="element-2135">
                      Formatting 342 Google Maps records
                    </p>
                  </div>}

                {exportPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} className="flex flex-col items-center justify-center min-h-[350px] text-center" data-id="element-2136">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6" data-id="element-2137">
                      <CheckCircle2Icon className="w-8 h-8 text-green-600" data-id="element-2138" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-2139">
                      Google Maps data ready
                    </h3>
                    <p className="text-slate-500 mb-8 max-w-sm" data-id="element-2140">
                      342 local business records with emails, phone numbers,
                      ratings, and reviews.
                    </p>
                    <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="w-full px-6 py-3.5 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700 transition-colors text-base text-center block" data-id="element-2141">
                      Download CSV in Openmart
                    </a>
                  </motion.div>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}